import { formatLocalDateTime } from '@helpers/formatting';
import { entryOrderSortByString } from '@helpers/sorting';
import { ActionDirection, BookkeepingEntry, entrySource, entryStatus, ShipmentType } from '@api/queries/inventory';
import React from 'react';
import { Column } from 'react-table';
import styled from 'styled-components';
import { EntryType, TFunction } from '~types/types';

export const SourceIcon = styled.div`
  font-size: 18px;
  text-align: center;
`;

export const getUnloadingFinishedColumn: (t: TFunction) => Column = (t) => ({
  Header: t('entry.unloading_finished'),
  id: 'unloadingFinished',
  maxWidth: 170,
  accessor: (row: BookkeepingEntry) => ({
    order: row.order,
    formatted: formatLocalDateTime(row.unloadingFinished, row.unloadingFinishedTimeSelected),
  }),
  sortMethod: entryOrderSortByString,
  filterMethod: (filter, row) =>
    filter.value !== '' ? row[filter.id].formatted && row[filter.id].formatted.indexOf(filter.value) >= 0 : true,
  Cell: (cellInfo) => cellInfo.value.formatted,
});

export const getAmountColumn: (t: TFunction) => Column = (t) => ({
  Header: () => (
    <span>
      {t('entry.amount')} <sup>kg</sup>
    </span>
  ),
  id: 'amount',
  maxWidth: 120,
  filterable: true,
  accessor: 'amount',
  filterMethod: (filter, row) => typeof row[filter.id] === 'number' && row[filter.id] === parseInt(filter.value),
  Cell: ({ value }) => (value !== null ? value.toLocaleString('fi') : ''),
});

export const getDirectionColumn: (t: TFunction) => Column = (t) => ({
  Header: t('entry.direction'),
  id: 'actionDirection',
  maxWidth: 120,
  accessor: 'actionDirection',
  Cell: ({ value }) => {
    return t(`entry.directions.${ActionDirection[value].toLowerCase()}`);
  },
  filterMethod: (filter, row) => (filter.value !== '' ? Number(row[filter.id]) === Number(filter.value) : true),
  Filter: ({ filter, onChange }) => (
    <select
      onChange={(event) => onChange(event.target.value)}
      style={{ width: '100%' }}
      value={filter ? filter.value : ''}
    >
      <option value="">{t('all')}</option>
      <option value={ActionDirection.Incoming}>{t('entry.directions.incoming')}</option>
      <option value={ActionDirection.Outgoing}>{t('entry.directions.outgoing')}</option>
    </select>
  ),
});

export const getEntryStateColumn: (t: TFunction) => Column = (t) => ({
  Header: t('entry.state'),
  id: 'entryState',
  maxWidth: 120,
  accessor: 'entryState',
  Cell: ({ value }) => {
    return t(`entry.states.${entryStatus[value].toLowerCase()}`);
  },
  filterMethod: (filter, row) => (filter.value ? row[filter.id] === Number(filter.value) : true),
  Filter: ({ filter, onChange }) => (
    <select
      onChange={(event) => onChange(event.target.value)}
      style={{ width: '100%' }}
      value={filter ? filter.value : ''}
    >
      <option value="">{t('all')}</option>
      <option value={entryStatus.incomplete}>{t('entry.states.incomplete')}</option>
      <option value={entryStatus.verified}>{t('entry.states.verified')}</option>
      <option value={entryStatus.approved}>{t('entry.states.approved')}</option>
    </select>
  ),
});

export const getEntryTypeColumn: (t: TFunction) => Column = (t) => ({
  Header: t('entry.type'),
  id: 'entryType',
  accessor: 'entryType',
  show: true,
  maxWidth: 100,
  Cell: ({ value }) => {
    return t(`entry.types.${EntryType[value].toLowerCase()}`);
  },
  filterMethod: (filter, row) => (filter.value ? row[filter.id] === Number(filter.value) : true),
  Filter: ({ filter, onChange }) => (
    <select
      onChange={(event) => onChange(event.target.value)}
      style={{ width: '100%' }}
      value={filter ? filter.value : ''}
    >
      <option value="">{t('all')}</option>
      <option value={EntryType.Measurement}>{t('entry.types.measurement')}</option>
      <option value={EntryType.Product}>{t('entry.types.product')}</option>
      <option value={EntryType.Mixture}>{t('entry.types.mixture')}</option>
      <option value={EntryType.Other}>{t('entry.types.other')}</option>
    </select>
  ),
});

export const getEntrySourceColumn: (t: TFunction) => Column = (t) => ({
  Header: () => <span title={t('entry.source_header_full')}>{t('entry.source_header_abbreviated')}</span>,
  id: 'source',
  maxWidth: 60,
  resizable: false,
  accessor: 'source',
  Cell: ({ value }) => {
    switch (value) {
      case entrySource.TruckScale:
        return <SourceIcon title={t('entry.sources.truckscale')}>Ⓐ</SourceIcon>;
      case entrySource.System:
        return <SourceIcon title={t('entry.sources.system')}>ⓢ</SourceIcon>;
      case entrySource.TrainAuthority:
        return <SourceIcon title={t('entry.sources.trainauthority')}>Ⓣ</SourceIcon>;
      case entrySource.Automation:
        return <SourceIcon title={t('entry.sources.automation')}>ⓢ</SourceIcon>;
      default:
        return null;
    }
  },
  filterMethod: (filter, row) => (filter.value ? row[filter.id] === Number(filter.value) : true),
  Filter: ({ filter, onChange }) => (
    <select
      onChange={(event) => onChange(event.target.value)}
      style={{ width: '100%' }}
      value={filter ? filter.value : ''}
    >
      <option value="" />
      <option value={entrySource.User}>{t('entry.filter_user')}</option>
      <option value={entrySource.TruckScale}>Ⓐ {t('entry.filter_truckscale')}</option>
      <option value={entrySource.TrainAuthority}>Ⓣ {t('entry.filter_trainauthority')}</option>
      <option value={entrySource.Automation}>ⓢ {t('entry.filter_automation')}</option>
      {/* Not used currently: <option value={entrySource.System}>ⓢ {t('entry.filter_system')}</option> */}
    </select>
  ),
});

export const getShipmentTypeColumn: (t: TFunction) => Column = (t) => ({
  Header: t('reports.carrier'),
  id: 'shipmentType',
  accessor: (entry: BookkeepingEntry) => {
    return (
      typeof entry.shipmentType === 'number' &&
      t(`entry.shipment_types.${ShipmentType[entry.shipmentType].toLowerCase()}`)
    );
  },
});
